/**
 * Rocon ChatWidget — opt-in floating chat panel.
 *
 *  - Hidden by default. The Support page's "Live Chat" button enables it by
 *    setting localStorage["rocon_chat_enabled"]="true" and dispatching the
 *    "rocon-chat-state-change" event. Persists across page navigations until
 *    the user clicks "End chat" inside the panel.
 *  - Session id (anonymous UUID) is persisted in localStorage["rocon_chat_session_id"].
 *  - Polls /chatbot/messages every 8s while the panel is open AND the view is
 *    "chat" (engineer replies arrive live).
 *  - JWT is added automatically by Utils/axiosApi.js.
 *  - Render gate: authenticated AND explicitly enabled.
 *  - The panel hosts three views: an intake form (shown for empty sessions),
 *    the regular chat, and a history list of past tickets.
 */
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  fetchSessionMessages,
  sendChatMessage,
} from "./chatApi";
import IntakeForm from "./IntakeForm";
import HistoryView from "./HistoryView";

const POLL_INTERVAL_MS = 8000;
const SESSION_KEY = "rocon_chat_session_id";
const ENABLE_KEY = "rocon_chat_enabled";
const ENABLE_EVENT = "rocon-chat-state-change";

function readAuthed() {
  return !!localStorage.getItem("accessToken");
}
function readEnabled() {
  return localStorage.getItem(ENABLE_KEY) === "true";
}

function getOrCreateSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function rotateSessionId() {
  const id =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(SESSION_KEY, id);
  return id;
}

export default function ChatWidget() {
  const [isAuthed, setIsAuthed] = useState(readAuthed);
  const [enabled, setEnabled] = useState(readEnabled);
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(getOrCreateSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  // view: "intake" | "chat" | "history"
  // Default to "intake"; once any message exists for the session we flip to
  // "chat" (see effect below).
  const [view, setView] = useState("intake");
  // Track the view to return to when the user opens History.
  const [historyReturnView, setHistoryReturnView] = useState("intake");
  // When the user opens a past ticket from History we swap the active session
  // id to that ticket's session and drop them into the regular chat view
  // (NOT a read-only detail view) so they can keep texting on it. We keep a
  // light "resumedTicketId" pill in the header just so they know they're
  // continuing an existing thread.
  const [resumedTicketId, setResumedTicketId] = useState(null);
  // Current org id from redux — sent with every /chat for ticket attribution.
  // Individual users have orgId === -1; in that case we omit org_id from the
  // payload so the Dataverse `cr0b4_orgid` column stays null for personal
  // tickets (only populated for true org-managed tickets).
  const rawOrgId = useSelector(
    (state) => state.organizationsInfo?.selectedOrg?.orgId,
  );
  const orgId = rawOrgId != null && rawOrgId !== -1 ? rawOrgId : undefined;
  const location = useLocation();
  // Snapshot the org id that was active when the chat was enabled. If the
  // user switches orgs (or to/from individual mode) we end the chat — each
  // org/individual space owns its own sites & tickets.
  const enabledOrgRef = useRef(rawOrgId);
  const scrollRef = useRef(null);

  // Re-check auth + enabled state continuously so the bubble appears as soon
  // as the user logs in or clicks "Live Chat", with no full-page refresh.
  // Auto-opens the panel when transitioning from disabled → enabled.
  useEffect(() => {
    const check = () => {
      setIsAuthed(readAuthed());
      setEnabled((prev) => {
        const next = readEnabled();
        if (!prev && next) setOpen(true); // user just clicked "Live Chat"
        return next;
      });
    };
    window.addEventListener("storage", check); //          cross-tab login/enable
    window.addEventListener("focus", check); //            fast catch on focus
    window.addEventListener(ENABLE_EVENT, check); //       same-tab enable trigger
    const id = setInterval(check, 2000); //                 fallback poll
    return () => {
      window.removeEventListener("storage", check);
      window.removeEventListener("focus", check);
      window.removeEventListener(ENABLE_EVENT, check);
      clearInterval(id);
    };
  }, []);

  const endChat = () => {
    try {
      localStorage.removeItem(ENABLE_KEY);
      window.dispatchEvent(new Event(ENABLE_EVENT));
    } catch (_e) {
      /* ignore */
    }
    setOpen(false);
    setEnabled(false);
    setResumedTicketId(null);
  };

  // Auto-close on org / individual-mode change OR on navigation to the
  // organizations-overview page. Each org/space has its own sites + tickets,
  // so an in-flight chat doesn't make sense across that boundary.
  useEffect(() => {
    if (!enabled) {
      enabledOrgRef.current = rawOrgId;
      return;
    }
    const orgChanged =
      enabledOrgRef.current !== undefined &&
      enabledOrgRef.current !== rawOrgId;
    const isOrgOverview =
      typeof location?.pathname === "string" &&
      location.pathname.includes("/organizations-overview");
    if (orgChanged || isOrgOverview) {
      endChat();
    } else {
      enabledOrgRef.current = rawOrgId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawOrgId, location?.pathname, enabled]);

  const startNewTicket = () => {
    // Rotate session id so the new ticket has a clean conversation context.
    const id = rotateSessionId();
    setSessionId(id);
    setMessages([]);
    setInput("");
    setResumedTicketId(null);
    setView("intake");
  };

  const openHistory = () => {
    setHistoryReturnView(view === "history" ? "chat" : view);
    setView("history");
  };

  const backFromHistory = () => {
    setView(historyReturnView || "chat");
  };

  /**
   * Resume a past ticket: swap the active session id to the ticket's
   * session_id (so the user's next /chat lands on the same Dataverse
   * conversation, which engineer-mode then routes to the operator),
   * preload that session's messages, and drop into the chat view.
   * The poll loop takes over for live engineer replies.
   */
  const resumeTicket = async (ticket) => {
    if (!ticket || !ticket.session_id) return;
    try {
      localStorage.setItem(SESSION_KEY, ticket.session_id);
    } catch (_e) {
      /* ignore quota errors */
    }
    setSessionId(ticket.session_id);
    setResumedTicketId(ticket.ticket_id || ticket.session_id);
    setInput("");
    setView("chat");
    setMessages([]);
    try {
      const res = await fetchSessionMessages(ticket.session_id);
      if (res && Array.isArray(res.messages)) setMessages(res.messages);
    } catch (_err) {
      /* leave empty; user can still type — poll will hydrate */
    }
  };

  // Poll for messages while the panel is open AND we are in the chat view.
  // Skipped while a /chat request is in-flight to avoid request pile-up
  // (the agent pipeline can take 5–30s and we explicitly re-fetch after it
  // completes, so the poll has nothing to add during that window).
  useEffect(() => {
    if (!open || !isAuthed || sending || view !== "chat") return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetchSessionMessages(sessionId);
        if (cancelled) return;
        if (res && Array.isArray(res.messages)) {
          setMessages(res.messages);
        }
      } catch (_err) {
        // axiosApi already handles 401 → reload; swallow other transient errors.
      }
    };

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [open, isAuthed, sessionId, sending, view]);

  // On first panel open, do a one-shot fetch to discover whether this session
  // already has messages. If so, jump straight to the chat view (skip intake).
  useEffect(() => {
    if (!open || !isAuthed) return;
    if (view !== "intake") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchSessionMessages(sessionId);
        if (cancelled) return;
        if (res && Array.isArray(res.messages) && res.messages.length > 0) {
          setMessages(res.messages);
          setView("chat");
        }
      } catch (_err) {
        /* keep intake on error */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isAuthed, sessionId, view]);

  // Auto-scroll to the latest message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, view]);

  const handleIntakeSubmit = async ({
    siteUrl,
    issueSummary,
    userUrgency,
    errorText,
    category,
  }) => {
    if (sending) return;
    setSending(true);
    const optimistic = {
      sender: "User",
      text: issueSummary,
      timestamp: new Date().toISOString(),
      _pending: true,
    };
    setMessages([optimistic]);
    setView("chat");

    try {
      // First turn: send both `message` (free-text shown in transcript) and
      // `issueSummary` (structured intake field for the triage agent).
      await sendChatMessage({
        message: issueSummary,
        sessionId,
        siteUrl,
        issueSummary,
        userUrgency,
        errorText,
        orgId,
        category,
      });
      const res = await fetchSessionMessages(sessionId);
      if (res && Array.isArray(res.messages)) {
        setMessages(res.messages);
      }
    } catch (_err) {
      setMessages((prev) =>
        prev.map((m) =>
          m === optimistic ? { ...m, _pending: false, _failed: true } : m,
        ),
      );
    } finally {
      setSending(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    setSending(true);
    // Optimistic append so the user sees their message immediately.
    const optimistic = {
      sender: "User",
      text,
      timestamp: new Date().toISOString(),
      _pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput("");

    try {
      await sendChatMessage({ message: text, sessionId, orgId });
      // The next poll tick will hydrate the bot reply + replace optimistic entry.
      const res = await fetchSessionMessages(sessionId);
      if (res && Array.isArray(res.messages)) {
        setMessages(res.messages);
      }
    } catch (_err) {
      // Mark the optimistic message as failed so the user knows.
      setMessages((prev) =>
        prev.map((m) =>
          m === optimistic ? { ...m, _pending: false, _failed: true } : m,
        ),
      );
    } finally {
      setSending(false);
    }
  };

  if (!isAuthed || !enabled) return null;

  return (
    <div style={styles.root}>
      {open && (
        <div style={styles.panel} role="dialog" aria-label="Rocon Support Chat">
          <div style={styles.header}>
            <span>Rocon Support</span>
            <div style={styles.headerActions}>
              {view === "chat" && (
                <button
                  type="button"
                  onClick={startNewTicket}
                  style={styles.headerLinkBtn}
                  title="Start a new ticket"
                >
                  New
                </button>
              )}
              {view !== "history" && (
                <button
                  type="button"
                  onClick={openHistory}
                  style={styles.headerLinkBtn}
                  title="View past tickets"
                >
                  History
                </button>
              )}
              <button
                type="button"
                onClick={endChat}
                style={styles.endChatBtn}
                title="End the conversation; widget will hide until you click Live Chat again"
              >
                End
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={styles.closeBtn}
                aria-label="Minimize chat"
                title="Minimize"
              >
                ×
              </button>
            </div>
          </div>

          {view === "intake" && (
            <IntakeForm onSubmit={handleIntakeSubmit} submitting={sending} />
          )}

          {view === "history" && (
            <HistoryView
              onBack={backFromHistory}
              onSelectTicket={resumeTicket}
            />
          )}

          {view === "chat" && (
            <>
              {resumedTicketId && (
                <div style={styles.resumedBanner}>
                  Continuing ticket #{String(resumedTicketId).slice(0, 8)} — your reply will go to the same engineer.
                </div>
              )}
              <div ref={scrollRef} style={styles.messages}>
                {messages.length === 0 && (
                  <div style={styles.empty}>
                    Hi! Ask me anything about your hosting issues.
                  </div>
                )}
                {messages.map((m, i) => (
                  <MessageBubble key={i} message={m} />
                ))}
              </div>

              <form onSubmit={handleSend} style={styles.inputRow}>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message…"
                  disabled={sending}
                  style={styles.input}
                />
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  style={styles.sendBtn}
                >
                  {sending ? "…" : "Send"}
                </button>
              </form>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={styles.fab}
        aria-label={open ? "Minimize chat" : "Open chat"}
      >
        {open ? "—" : "💬"}
      </button>
    </div>
  );
}

function MessageBubble({ message }) {
  const sender = message.sender || "Bot";
  const palette = SENDER_STYLE[sender] || SENDER_STYLE.Bot;
  // Optional polish: show a "Ticket #xxxxxxxx created" hint when this bot reply
  // came from an escalate_to_human envelope and carries a ticket id. We never
  // render `internal_summary` (operator-only per spec) — `text` is always the
  // customer-facing `reply_text`, which the backend already rewrites for
  // escalations.
  const showTicketHint =
    sender !== "User" &&
    message.action === "escalate_to_human" &&
    typeof message.ticket_id === "string" &&
    message.ticket_id.length > 0;
  return (
    <div style={{ ...styles.bubbleRow, justifyContent: palette.align }}>
      <div
        style={{
          ...styles.bubble,
          background: palette.bg,
          color: palette.fg,
          opacity: message._pending ? 0.6 : 1,
          border: message._failed ? "1px solid #d93025" : "none",
        }}
      >
        <div style={styles.bubbleSender}>{sender}</div>
        <div>{message.text}</div>
        {showTicketHint && (
          <div style={styles.ticketHint}>
            Ticket #{message.ticket_id.slice(0, 8)} created
          </div>
        )}
      </div>
    </div>
  );
}

// Palette matches the host app primary blue (--primary-main #135fc2 from
// GlobalStyles.module.scss). Bot bubbles use the light-tint variant.
const SENDER_STYLE = {
  User: { bg: "#135fc2", fg: "#fff", align: "flex-end" },
  Bot: { bg: "#f0f3f8", fg: "#171a1c", align: "flex-start" },
  Engineer: { bg: "#1e7d3a", fg: "#fff", align: "flex-start" },
};

const styles = {
  root: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 2147483000,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  fab: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    border: "none",
    background: "#135fc2",
    color: "white",
    fontSize: 22,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(19,95,194,0.35)",
  },
  panel: {
    position: "absolute",
    bottom: 72,
    right: 0,
    width: 360,
    height: 520,
    background: "white",
    borderRadius: 12,
    boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    background: "#135fc2",
    color: "white",
    padding: "12px 14px",
    fontWeight: 600,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  headerLinkBtn: {
    background: "rgba(255,255,255,0.18)",
    color: "white",
    border: "none",
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 4,
    cursor: "pointer",
    fontWeight: 500,
  },
  endChatBtn: {
    background: "rgba(255,255,255,0.18)",
    color: "white",
    border: "none",
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 4,
    cursor: "pointer",
    fontWeight: 500,
  },
  closeBtn: {
    background: "transparent",
    color: "white",
    border: "none",
    fontSize: 22,
    cursor: "pointer",
    lineHeight: 1,
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: 12,
    background: "#fbfcfe",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  resumedBanner: {
    padding: "8px 12px",
    background: "#d2dff1",
    color: "#0b0d0e",
    fontSize: 11,
    fontWeight: 500,
    borderBottom: "1px solid #b9cbe6",
  },
  detailHeader: {
    padding: "10px 12px",
    borderBottom: "1px solid #e6ecf5",
    background: "#fbfcfe",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  detailTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#171a1c",
  },
  backLink: {
    alignSelf: "flex-start",
    background: "transparent",
    border: "none",
    color: "#135fc2",
    fontSize: 12,
    padding: 0,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  empty: {
    color: "#888",
    fontSize: 13,
    textAlign: "center",
    marginTop: 24,
  },
  bubbleRow: {
    display: "flex",
    width: "100%",
  },
  bubble: {
    maxWidth: "78%",
    padding: "8px 12px",
    borderRadius: 12,
    fontSize: 14,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  bubbleSender: {
    fontSize: 10,
    fontWeight: 600,
    opacity: 0.75,
    marginBottom: 2,
  },
  ticketHint: {
    marginTop: 6,
    fontSize: 11,
    fontWeight: 600,
    opacity: 0.8,
  },
  inputRow: {
    display: "flex",
    gap: 8,
    padding: 10,
    borderTop: "1px solid #eee",
    background: "white",
  },
  input: {
    flex: 1,
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #d2dff1",
    fontSize: 14,
    outline: "none",
    color: "#171a1c",
  },
  sendBtn: {
    background: "#135fc2",
    color: "white",
    border: "none",
    padding: "8px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
  },
};
